import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { AttendanceRule, GymRetrieve } from 'types';
import { TimePeriod, TimeUnitEnum } from 'types/enums';

import { numberToOrdinalFormatter } from './formatters';

export const isDatePast = (date: string | null): boolean => {
  if (date === null) return false;
  const today = dayjs(new Date()).format('YYYY-MM-DD');
  return date < today;
};

export const isDateTimePast = (startDate: string, startTime: string): boolean => {
  const dateTime = dayjs(`${startDate} ${startTime}`);
  const now = dayjs(new Date());

  return dateTime.isBefore(now);
};

export const dateRangeContainsPresentDate = (startDate: string, endDate: string | null): boolean => {
  const today = dayjs(new Date()).format('YYYY-MM-DD');

  // No expiration, active from startDate onward
  if (endDate === null) {
    return startDate <= today;
  }

  return startDate <= today && endDate >= today;
};

export const isDateFuture = (date: string): boolean => {
  const today = dayjs(new Date()).format('YYYY-MM-DD');
  return date > today;
};

export const getDateTimePeriod = (
  startDate: string,
  endDate: string | null,
): 'past' | 'present' | 'future' => {
  if (isDatePast(endDate)) {
    return 'past';
  }
  if (isDateFuture(startDate)) {
    return 'future';
  }
  return 'present';
};

type TimeUnits = `${TimeUnitEnum}`;

export const getTimeUnitInMinutes = (time: number, unit: TimeUnits): number => {
  dayjs.extend(duration);

  const durationInUnit = dayjs.duration(time, unit);

  return durationInUnit.as(TimeUnitEnum.MINUTE);
};

export const getIntegerValueAndTimeUnitFromMinutes = (
  minutes: number,
): { value: number; timeUnit: TimePeriod } => {
  dayjs.extend(duration);

  const durationInMinutes = dayjs.duration(minutes, TimeUnitEnum.MINUTE);

  const checkIsNotZeroAndInteger = (number: number): boolean => number !== 0 && Number.isInteger(number);

  if (checkIsNotZeroAndInteger(durationInMinutes.asDays())) {
    return { value: durationInMinutes.asDays(), timeUnit: TimeUnitEnum.DAY };
  }
  if (checkIsNotZeroAndInteger(durationInMinutes.asHours())) {
    return { value: durationInMinutes.asHours(), timeUnit: TimeUnitEnum.HOUR };
  }

  return { value: durationInMinutes.asMinutes(), timeUnit: TimeUnitEnum.MINUTE };
};

// Returns first day of the month at 12AM and last day of the month as 11:59:59PM
export const getMonthRangeDates = (year?: number, month?: number): Date[] => {
  const date = new Date();
  return [
    new Date(year || date.getFullYear(), month || date.getMonth(), 1),
    new Date(year || date.getFullYear(), (month || date.getMonth()) + 1, 0, 23, 59, 59, 59),
  ];
};

// Returns first day of the month at 12AM and current day of the month as 11:59:59PM
export const getMonthRangeDatesMTD = (): Date[] => {
  const date = new Date();
  return [
    new Date(date.getFullYear(), date.getMonth(), 1),
    new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 59),
  ];
};

// Returns the start (12:00 AM) and end (11:59:59 PM) of the current week (Sunday to Saturday)
export const getWeekRangeDates = (): [Date, Date] => {
  const date = new Date();
  const weekStartDate = date.getDate() - date.getDay();
  const weekStart = new Date(date.setDate(weekStartDate));
  const weekEnd = new Date(date.setDate(weekStart.getDate() + 6));
  return [
    new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate(), 0, 0, 0, 0),
    new Date(weekEnd.getFullYear(), weekEnd.getMonth(), weekEnd.getDate(), 23, 59, 59, 59),
  ];
};

export interface WeekRange {
  start: string;
  end: string;
}

/** Sunday–Saturday range containing `date` (YYYY-MM-DD). */
export const getWeekRange = (date: string): WeekRange => {
  const d = dayjs(date);
  const sunday = d.subtract(d.day(), 'day');
  return {
    start: sunday.format('YYYY-MM-DD'),
    end: sunday.add(6, 'day').format('YYYY-MM-DD'),
  };
};

// Returns current day at 12AM and current day at 11:59:59PM
export const getDayRangeDates = (year?: number, month?: number, day?: number): Date[] => {
  const date = new Date();
  return [
    new Date(year || date.getFullYear(), month || date.getMonth(), day || date.getDate()),
    new Date(year || date.getFullYear(), month || date.getMonth(), day || date.getDate(), 23, 59, 59, 59),
  ];
};

// Accepts a date and various time units, returns a new date offset by the given time
export const setDateDifference = (
  originalDate: Date,
  years?: number,
  months?: number,
  days?: number,
  hours?: number,
  minutes?: number,
  seconds?: number,
): Date => {
  const date = new Date(originalDate);
  if (!!years) {
    date.setFullYear(date.getFullYear() + years);
  }
  if (!!months) {
    date.setMonth(date.getMonth() + months);
  }
  if (!!days) {
    date.setDate(date.getDate() + days);
  }
  if (!!hours) {
    date.setHours(date.getHours() + hours);
  }
  if (!!minutes) {
    date.setMinutes(date.getMinutes() + minutes);
  }
  if (!!seconds) {
    date.setSeconds(date.getSeconds() + seconds);
  }
  return date;
};

/**
 * Format a date for API requests (YYYY-MM-DD). Use when sending date-only params
 * to the backend so it can interpret dates in the gym's timezone.
 */
export const formatDateForApi = (date: Date | dayjs.Dayjs | null | undefined): string | undefined => {
  if (date == null) return undefined;
  return dayjs(date).format('YYYY-MM-DD');
};

export const formatDate = (date: Date): string => {
  var d = new Date(date),
    month = '' + (d.getMonth() + 1),
    day = '' + d.getDate(),
    year = d.getFullYear();

  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;

  return [year, month, day].join('-');
};

export const getSuccessorStartDate = (
  dateFormat: string,
  attendanceRules: AttendanceRule[],
  packageStartDate?: string,
): string => {
  const startDate = packageStartDate ? new Date(packageStartDate) : new Date();
  let maxEndDate = dayjs(startDate);

  // Calculate end date for each attendance rule considering delay start
  attendanceRules.forEach(ar => {
    let ruleEndDate = dayjs(startDate);

    // Add delay start if it exists (matches backend line 627-628)
    if (ar.delayInterval && ar.delayIntervalCount) {
      ruleEndDate = ruleEndDate.add(ar.delayIntervalCount, ar.delayInterval);
    }

    // Add validity interval count
    // When interval_count exists, there are multiple dispensations, but the final
    // expiration_date after all iterations is: start + delay + validity_interval_count
    // (matches backend logic where after all iterations, expiration_date = start + delay + validity_interval_count)
    if (ar.validityInterval && ar.validityIntervalCount) {
      ruleEndDate = ruleEndDate.add(ar.validityIntervalCount, ar.validityInterval);
    }
    if (ruleEndDate.isAfter(maxEndDate)) {
      maxEndDate = ruleEndDate;
    }
  });

  return maxEndDate.format(dateFormat);
};

type Timezone = GymRetrieve['timezone'];

const CANONICAL_TIMEZONES: Partial<Record<Timezone, Timezone>> = {
  'US/Alaska': 'America/Anchorage',
  'US/Aleutian': 'America/Adak',
  'US/Arizona': 'America/Phoenix',
  'US/Central': 'America/Chicago',
  'US/East-Indiana': 'America/Indiana/Indianapolis',
  'US/Eastern': 'America/New_York',
  'US/Hawaii': 'Pacific/Honolulu',
  'US/Indiana-Starke': 'America/Indiana/Knox',
  'US/Michigan': 'America/Detroit',
  'US/Mountain': 'America/Denver',
  'US/Pacific': 'America/Los_Angeles',
  'US/Samoa': 'Pacific/Pago_Pago',
};

export const normalizeTimezone = (timezone: Timezone): Timezone => CANONICAL_TIMEZONES[timezone] ?? timezone;

export const parseDateTimeToDate = (dateString: string, timezone: string | undefined): Date => {
  if (!timezone) {
    return new Date(dateString);
  }

  const date = new Date(dateString);
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(date);

  const year = parts.find(p => p.type === 'year')!.value;
  const month = parts.find(p => p.type === 'month')!.value;
  const day = parts.find(p => p.type === 'day')!.value;

  return new Date(Number(year), Number(month) - 1, Number(day.slice(0, 2)));
};

/**
 * Format a value that represents a gym-local calendar date (e.g. member_since — stored as a
 * gym-tz-midnight DateTimeField) as its gym-local date string, independent of the viewer's
 * browser timezone. Pass a raw stored string or an already-gym-local Date. (ENG-2344.)
 */
export const formatGymDate = (
  value: string | Date,
  timezone: string | undefined,
  format: string,
): string => {
  const date = typeof value === 'string' ? parseDateTimeToDate(value, timezone) : value;
  return dayjs(date).format(format);
};

export const convertDateStringToDateObject = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-');

  return new Date(Number(year), Number(month) - 1, Number(day.slice(0, 2)));
};

export const formatDateWithOrdinal = (dateString: string): string => {
  const date = dayjs(dateString);
  const day = date.date();
  const month = date.format('MMMM');
  const year = date.year();
  const ordinal = numberToOrdinalFormatter(day);
  return `${month} ${day}${ordinal}, ${year}`;
};

export const formatDateForDisplay = (dateString: string | null | undefined): string | undefined => {
  if (!dateString) return undefined;
  return dayjs(dateString).format('MMM D, YYYY');
};
